<script setup>
import { Menu, Search } from '@element-plus/icons'
</script>

<template>
    <el-container>
        <el-header class="navbar">
            <el-affix :offset="0" >
                <div class="header">
                    <el-button link @click="handleAside" :icon="Menu"></el-button>
                    <div class="title">{{ title }}</div>
                    <el-autocomplete
                        v-model="state"
                        :fetch-suggestions="querySearch"
                        :trigger-on-focus="false"
                        :prefix-icon="Search"
                        clearable
                        class="inline-input"
                        placeholder="Recherche"
                        @select="handleSelect"
                        style="margin-right: 6px;"
                    />
                </div>
                <div v-for="(lieu) in lieux" :key="lieu" class="header">
                    <div class="infos">{{ lieu }}</div>
                </div>
            </el-affix>
        </el-header>

        <el-main style="padding: 0;">
            <el-drawer v-model="asideShow" direction="ltr" title="Navigation" size="350" style="padding: 0;">
                <el-aside class="tree" v-show="asideShow">
                    <el-tabs v-model="activeName" type="card" >
                        <el-tab-pane label="Dates" name="dates">
                            <el-tree
                                :data="data"
                                :props="defaultProps"
                                :node-key="nodeKey"
                                ref="datetree"
                                accordion
                                highlight-current
                                @node-click="handleNodeClick"
                                empty-text="..."
                            />
                        </el-tab-pane>
                        <el-tab-pane label="Étiquettes" name="tags">
                            <el-scrollbar>
                                <el-tree
                                    :data="tags"
                                    :props="defaultProps"
                                    :node-key="nodeKey"
                                    ref="tagtree"
                                    accordion
                                    highlight-current
                                    @node-click="handleTagsClick"
                                    empty-text="..."
                                />
                            </el-scrollbar>
                        </el-tab-pane>
                    </el-tabs>
                </el-aside>
            </el-drawer>

            <el-progress
                v-if="progress > 0"
                :percentage="100"
                :format="() => ''"
                :indeterminate="true"
                :duration="5"
            />

            <div v-if="urls.length == 0"></div>

            <div v-else class="page-container">
                <div class="img-gallery">
                    <div v-for="(photo, index) in photos.slice(start, start+pageSize)" 
                            :key="photo.id"
                            :href="photo.url"
                            class="img-gallery__item">
                        <figure>
                            <img
                                alt="img1" 
                                width="auto"
                                height="200px"
                                class="img-responsive"
                                :src="photo.url.replace('1024x1024', '200x200')"
                                @click="handlePhotoClick(start + index)"
                                />
                            <figcaption>
                                <div>
                                    <el-button 
                                        @click="handleDateClick(photo.tooltip.date)" 
                                        class="button-tag"
                                        type="primary" link>{{ photo.tooltip.label }}</el-button>
                                    <el-button v-for="(tag, index) in photo.tags" :key="index" 
                                        @click="handleTagClick(tag)" 
                                        class="button-tag"
                                        type="primary" link>
                                        {{ tag }}
                                    </el-button>
                                </div>
                            </figcaption>
                        </figure>
                    </div>
                </div>

                <div v-if="photos.length > pageSize">
                    <el-pagination
                        class="pagination"
                        layout="prev, pager, next"
                        @current-change="handleCurrentChange"
                        v-model:current-page="currentPage"
                        v-model:page-size="pageSize"
                        :total="photos.length" />
                </div>

                <div v-if="viewer">
                    <el-image-viewer
                        :initial-index="index"
                        :url-list="urls"
                        :infinite="false"
                        @close="viewer = false"
                    ></el-image-viewer>
                </div>
            </div>

        </el-main>
    </el-container>
</template>

<style>
.navbar {
    position: fixed;
    width: 100%;
    padding: 0;
    z-index: 12;
}
.header {
    display:flex;
    width: 100%;
    flex-direction: row;
    background-color: rgba(255, 255, 255, 0.92);
    border-bottom-style: solid;
    border-bottom-color: rgb(200, 200, 200);
    border-width: 1px;
}
.title {
    padding-top: 8px;
    font-weight: bold;
    color: gray;
    width: -webkit-fill-available;
}
.infos {
    padding-top: 8px;
    font-weight: bold;
    font-size: x-large;
    color: deepskyblue;
    width: -webkit-fill-available;
}
.lieu {
    display: block;
}
.tree {
    width: 100%;
    padding: 0;
}
.inline-input {
    width: 300px;
}
.affix-container {
    height: 100vh;
}
.button-tag {
    margin: 0;
    padding: 0;
    color:white;
    font-size: small;
}
.pagination {
    justify-content: center;
}
.el-image-viewer__mask {
    opacity: 0.95;
    background-color: #ccc;
}
.el-drawer__header {
    margin: 10px;
    padding: 0;
}
.el-drawer__body {
    padding: 0;
}

/* ------------------------------------- */
/* Reset */
:root {
  --container-padding: 1.5em;
  --container-width: 2000px;
}

* {
  &,
  &::before,
  &::after {
    box-sizing: border-box;
  }
}

body {
  margin: 0;
  font: 1em/160% sans-serif;
}

img {
  max-width: 100%;
  vertical-align: middle;
  height: auto;
}

.page-container {
  padding: var(--container-padding);
  max-width: var(--container-width);
  margin-right: auto;
  margin-left: auto;
}

/* Gallery layout */
:root {
  --gallery-gap: 1.0em;
  --gallery-item-height: 250px;
}

.img-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gallery-gap);
  &::after {
    content: "";
    flex-grow: 999;
    flex-shrink: 1;
    flex-basis: auto;
  }
}

.img-gallery__item {
  position: relative;
  flex: auto;
  overflow: hidden;
  height: var(--gallery-item-height);
  border-radius: var(--gallery-item-border-radius);

  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
}

/* Gallery styles */
:root {
  --gallery-item-border-radius: 0.4em;
  --gallery-caption-bg-color: hsla(0, 0%, 0%, 0.5);
  --gallery-caption-text-color: white;
}

.img-gallery__item {
  figure {
    margin: 0;
    height: 100%;
  }

  figcaption {
    position: absolute;
    inset: auto auto 0 0;

    width: 100%;
    padding: 1rem;

    color: var(--gallery-caption-text-color);
    background-color: var(--gallery-caption-bg-color);

    transition: opacity 0.25s ease-in-out;
    opacity: 0;
  }

  &:hover {
    figcaption {
      opacity: 1;
    }
  }
}
</style>

<script>
import axios from "axios";
import dayjs from "dayjs";
import locale_fr from "dayjs/locale/fr";

dayjs.locale(locale_fr);

export default {
    name: "Tree",
    props: {
        token: String,
        locationApi: String,
    },
    data() {
        return {
            asideShow: false,
            activeName: 'dates',
            data: [],
            tags: [],
            urls: [],
            photos: [],
            lieux: [],
            currentPage: 1,
            start: 0,
            end: 50,
            index: 0,
            pageSize: 100,
            title: "",
            type: "",
            viewer: false,
            aside: true,
            defaultProps: {
                children: 'children',
                label: 'label',
                dates: 'dates',
            },
            nodeKey: 'id',
            progress: 0,
            state: '',
        }
    },
    methods: {
        load() {
        },
        handleLoad() {
        },
        handlePhotoClick(index) {
            this.index = index;
            this.viewer = true;
        },
        querySearch(qs, cb) {
            const results = [];
            qs = qs.toLowerCase();
            this.tags.forEach(tag => tagQuery(tag, qs, results));
            cb(results)
        },
        handleSelect(item) {

            this.activeName = "tags";
            this.handleTagsClick(item);
            const self = this;
            setTimeout(() => {
                self.$refs.tagtree.setCurrentKey(item);
            }, 500);
            Object.keys(this.$refs.tagtree.store.nodesMap).forEach(key => {
                this.$refs.tagtree.store.nodesMap[key].expanded = false
            });

        },
        handleAside() {
            this.asideShow = this.asideShow == false;
        },
        handleDateClick(date) {
            this.activeName = "dates";
            this.$refs.datetree.setCurrentKey(null);
            this.handleNodeClick({ type: 'day', id: date});
            const self = this;
            setTimeout(() => {
                self.$refs.datetree.setCurrentKey(date);    
            }, 500);
            Object.keys(this.$refs.datetree.store.nodesMap).forEach(key => {
                this.$refs.datetree.store.nodesMap[key].expanded = false
            });
        },
        handleTagClick(name) {
            const results = [];
            this.tags.forEach(tag => tagSearchName(tag, name, results));
            if (results.length > 0) {
                if (results.length > 1) {
                    console.error(`tag name error : ${name}, results = ${results.length}`);
                    console.error(results);
                }
                this.handleSelect(results[0]);
            } else {
                console.error(`tag name not found : ${name}`);
            }
        },
        handleNodeClick(data) {
            this.type = data.type;
            this.urls = [];
            this.photos = [];
            this.currentPage = 1;
            this.start = 0;
            this.end = this.pageSize;
            switch (data.type) {
                case 'day':
                    this.title = `${dayjs(data.id).format('dddd D MMMM YYYY')} - ${data.photos} photos`;
                    this.getPhotos(data);
                    break;
                case 'year':
                    this.title = `Année ${data.id}`;
                    this.getPhotos(data);
                    break;
                case 'month':
                    this.title = `${dayjs(data.id).format('MMMM YYYY')}`;
                    this.getPhotos(data);
                    break;
                default:
                    this.title = "";
                    break;
            }
        },
        handleTagsClick(data) {
            this.urls = [];
            this.photos = [];
            this.currentPage = 1;
            this.start = 0;
            this.end = this.pageSize;

            let title = data.id[0] == '/' ? data.id.substring(1) : data.id;
            const i = title.indexOf('@');
            if (i > 0) {
                const date = new Date(title.substring(i));
                title = `${title.substring(0, i)} - ${dayjs(date).format('dddd D MMMM YYYY')}`;
            } else {
                if (data.date) {
                    title = `${title} - ${data.date}`;
                }
            }
            if (data.photos) {
                title = `${title} - ${data.photos} photos`;
            }
            this.title = title.replaceAll('/', ' > ');
            this.lieux = [];
            
            this.progress++;
            axios
                .get(`${this.locationApi}/tagPhotos/${encodeURIComponent(data.id)}`, {
                    headers: {
                        'Content-Type': 'application/json, text/plain, */*',
                        'Authorization': 'Bearer ' + this.token,
                    }
                })
                .then(response => {
                    for (const photo of response.data) {
                        photo.url = `${this.locationApi}/photo/${photo.id}?size=1024x1024`;
                        photo.tooltip = {
                            label: dayjs(photo.exposure_time * 1000).format('dddd D MMMM YYYY - H:mm:ss'),
                            date: dayjs(photo.exposure_time * 1000).format('YYYY-MM-DD'),
                        };
                        this.urls.push(photo.url);
                        this.photos.push(photo);
                    }
                })
                .finally(() => { this.progress-- });
        },
        handleCurrentChange(val) {
            this.end = val * this.pageSize;
            this.start = this.end - this.pageSize;
        },
        getPhotos(data) {
            this.progress++;
            axios
                .get(`${this.locationApi}/${data.type}/${data.id}`, {
                    headers: {
                        'Content-Type': 'application/json, text/plain, */*',
                        'Authorization': 'Bearer ' + this.token,
                    }
                })
                .then(response => {
                    switch (data.type) {
                        case 'day':
                            this.title = `${dayjs(data.id).format('dddd D MMMM YYYY')} - ${response.data.length} photos`;
                            break;
                        case 'year':
                            this.title = `Année ${data.id} - ${response.data.length} photos`;
                            break;
                        case 'month':
                            this.title = `${dayjs(data.id).format('MMMM YYYY')} - ${response.data.length} photos`;
                            break;
                        default:
                            this.title = `Les ${response.data.length}  photos`;
                            break;
                    }

                    this.lieux = [];
                    for (const photo of response.data) {
                        if (data.type == 'day') {
                            const tags = [];
                            photo.tags.forEach(name => {
                                this.tags.forEach(tag => tagSearchName(tag, name, tags));
                            });
                            tags.forEach((t) => {
                                if (t.id.startsWith('/Lieux/')) {
                                    const name = t.id.substring(7).replaceAll('/', ' > ');
                                    if (!this.lieux.includes(name)) {
                                        this.lieux.push(name);
                                    }
                                }
                            });
                        }

                        photo.url = `${this.locationApi}/photo/${photo.id}?size=1024x1024`;
                        if (photo.exposure_time) {
                            if (data.type === 'day') {
                                photo.tooltip = {
                                    label: dayjs(photo.exposure_time * 1000).format('H:mm:ss'),
                                    date: dayjs(photo.exposure_time * 1000).format('YYYY-MM-DD'),
                                };
                            } else {
                                if (photo.exposure_time != null)  {
                                    photo.tooltip = {
                                        label: dayjs(photo.exposure_time * 1000).format('dddd D MMMM YYYY - H:mm:ss'),
                                        date: dayjs(photo.exposure_time * 1000).format('YYYY-MM-DD'),
                                    };
                                }
                            }
                        }
                        this.urls.push(photo.url);
                        this.photos.push(photo);
                    }
                })
                .finally(() => { this.progress-- });
        },
        handleImageClick(index) {
            this.index = index;
            this.viewer = true;
        },
    },
    mounted() {
        this.progress += 3;
        axios
            .get(`${this.locationApi}/tree`
                , {
                    headers: {
                        "Content-type": "application/json",
                        'Authorization': 'Bearer ' + this.token,
                    }
                })
            .then(response => {
                const years = Object.keys(response.data).reverse();
                this.data = [];
                for (const year of years) {
                    this.data.push({ 
                        id: year, 
                        type: 'year', 
                        label: year == 1970 ? 'Sans dates' : year, 
                        children: getMonths(year, response.data[year]) });
                }
            })
            .finally(() => { this.progress-- });
        axios
            .get(`${this.locationApi}/tags`
                , {
                    headers: {
                        "Content-type": "application/json",
                        'Authorization': 'Bearer ' + this.token,
                    }
                })
            .then(response => {
                this.tags = [];
                for (const tag of response.data) {
                    addTag(this.tags, tag, tag.name, '');
                }
                this.tags = this.tags.sort((t1, t2) => {
                    function sname(name) {
                        const n = name.toLowerCase();
                        if (n[0] == '/')    return n.substring(1);
                        return n;
                    }
                    const n1 = sname(t1.name);
                    const n2 = sname(t2.name);
                    if (n1 > n2) {
                        return 1;
                    }
                    if (n1 < n2) {
                        return -1;
                    }
                    return 0;
                });
            })
            .finally(() => { this.progress-- });
        axios
            .get(`${this.locationApi}/last/10`, {
                headers: {
                    "Content-type": "application/json",
                    'Authorization': 'Bearer ' + this.token,
                }
            })
            .then(response => {
                this.title = `Les ${response.data.length} dernières photos`;
                for (const photo of response.data) {
                    photo.url = `${this.locationApi}/photo/${photo.id}?size=1024x1024`;
                    photo.tooltip = {
                        label: dayjs(photo.exposure_time * 1000).format('dddd D MMMM YYYY - H:mm:ss'),
                        date: dayjs(photo.exposure_time * 1000).format('YYYY-MM-DD'),
                    };
                    this.urls.push(photo.url);
                    this.photos.push(photo);
                }
            })
            .finally(() => { this.progress-- });
    },
}

function tagQuery(tag, qs, results) {
    if (tag.name.toLowerCase().indexOf(qs) >= 0) results.push({ value: tag.name, type: 'tag', id: tag.id });
    tag.children.forEach(child => tagQuery(child, qs, results));
}

function tagSearchName(tag, name, results) {
    if (name == '') return;
    if (tag.name.endsWith(name)) results.push({ value: tag.name, type: 'tag', id: tag.id });
    tag.children.forEach(child => tagSearchName(child, name, results));
}

function addTag(tags, tag, path, prefix) {
    const photos = tag.photo_id_list;
    const children = [];
    if (tag.dates) {
        if (tag.dates.length > 1) {
            for(const dt of tag.dates) {
                children.push({
                    id: `${prefix}${path}@${dt}`, 
                    name: dt, 
                    label: dayjs(dt).format('dddd D MMMM YYYY'), 
                    children: [] });
            }
        }
    }

    if (path[0] === '/') {
        const names = path.substring(1).split('/');
        const name = names[0];
        if (tags.length === 0 || tags[tags.length-1].name !== name) {
            const date = (tag.dates && tag.dates.length == 1)
                ? ` ${dayjs(tag.dates[0]).format('dddd D MMMM YYYY')}` : '';
            tags.push({ id: `${prefix}${path}`, name, label: `${name} (${photos})`, photos, children, date });
        }
        if (names.length > 1) {
            addTag(tags[tags.length-1].children, tag, path.substring(name.length + 1), `${prefix}/${name}`);
        }
    } else {
        tags.push({ id: path, name: path, label: `${path} (${photos})`, photos, children });
    }
}

function getMonths(id, year) {
    const months = [];

    const keys = Object.keys(year.months).reverse();
    for (const month of keys) {
        const date = `${id}-${String(parseInt(month) + 1).padStart(2, "0")}`;
        months.push({
            id: date,
            type: 'month',
            label: locale_fr.months[month],
            children: getDates(date, year.months[month]),
        });
    }
    return months;
}

function getDates(id, month) {
    const dates = [];

    const keys = Object.keys(month.days).reverse();
    for (const day of keys) {
        const date = `${id}-${day.padStart(2, "0")}`;
        const photos = month.days[day].photos;
        dates.push({
            id: date,
            type: 'day',
            label: `${dayjs(date).format('ddd D MMM YYYY')} (${photos})`,
            photos,
            children: [],
        });
    }
    return dates;
}

</script>

<style scoped>
.demo-image__lazy {
    overflow-y: auto;
}
.el-image {
    display: block;
    max-width: 1200px;
    margin-bottom: 10px;
}
</style>
